import React from 'react';
import { Box, Paper, Typography, Table, TableContainer, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { BiColorHeader } from '../scholarshipApplication/components';
import RenderIf from '../../renderIf';
import { DashboardBody, DashboardHeader, FilterControls, Search } from '../../shared';
import { MuiTablePagination } from '../../shared/muiControls';

import useRequestBuilder from './useRequestBuilder';

const SchoolDashboard = (props) => {
    const { state: savedState } = useLocation();
    const [isCompressed, setIsCompressed] = React.useState(false);

    const tableRef = React.useRef();

    const {
        page,
        rowsPerPage,
        isFetching,
        isInitialized,
        searchValue,
        headers,
        list,
        sortKey,
        sortDir,
        filters,
        // hasMore, #TODO If the full dataset retrieval ever causes problems in terms of Lambda timeouts, this may need to come back.
        selectedFilters,
        totalRecordCount,
        totalUnfilteredCount,
        retrieveStudents,
        handleFilterString,
        handleSort,
        // loadMore, #TODO If the full dataset retrieval ever causes problems in terms of Lambda timeouts, this may need to come back.
        handleNav,
        handlePageChange,
        handleRowsPerPageChange,
    } = useRequestBuilder(savedState);

    React.useEffect(() => {
        retrieveStudents();
    }, []);

    return (
        <Box
            sx={{
                marginTop: (theme) => theme.spacing(1),
                paddingRight: (theme) => theme.spacing(3),
                paddingLeft: (theme) => theme.spacing(3),
            }}
        >
            <BiColorHeader
                left="My"
                right="Applications."
            />

            <RenderIf isTrue={isInitialized && filters instanceof Object}>
                <FilterControls
                    isFetching={isFetching}
                    filters={filters}
                    currentCount={list.length}
                    totalRecordCount={totalRecordCount}
                    totalUnfilteredCount={totalUnfilteredCount}
                    handleFilterChange={handleFilterString}
                    selectedFilters={selectedFilters}
                />
            </RenderIf>

            <Paper
                sx={theme=> {
                    return {
                        marginTop: theme.spacing(2),
                        marginBottom: theme.spacing(2),
                        padding: theme.spacing(3),
                        borderRadius: '8px',
                        [theme.breakpoints.down('lg')]: {
                            maxWidth: '75vw',
                        },
                        [theme.breakpoints.down('md')]: {
                            maxWidth: '60vw',
                        },
                        [theme.breakpoints.down('sm')]: {
                            maxWidth: 'unset',
                        },
                    }
                }}
            >
                <RenderIf isTrue={isInitialized}>
                    <Search
                        disabled={isFetching}
                        searchText="Search on Last or First Name."
                        searchValue={searchValue}
                        handleSearchChange={handleFilterString}
                        handleSwitch={() => setIsCompressed((prev) => !prev)}
                    />

                    <TableContainer
                        ref={tableRef}
                        sx={theme=> {
                            return {
                                maxHeight: '55vh',
                                overflow: 'auto',
                                [theme.breakpoints.down('sm')]: {
                                    maxHeight: '50vh',
                                },
                            };
                        }}
                    >
                        <Table
                            stickyHeader
                            size={isCompressed ? 'small' : 'medium'}
                        >
                            <DashboardHeader
                                disabled={isFetching}
                                headers={headers}
                                handleSort={handleSort}
                                sortKey={sortKey}
                                sortDir={sortDir}
                            />

                            <DashboardBody
                                list={list?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                                headers={headers}
                                isFetching={isFetching}
                                handleNav={handleNav}
                            />
                        </Table>
                    </TableContainer>

                    <MuiTablePagination
                        disabled={isFetching}
                        count={totalRecordCount || 0}
                        totalCount={totalUnfilteredCount}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        showFirstButton={true}
                        showLastButton={true}
                    />
                </RenderIf>

                <RenderIf isTrue={!isInitialized}>
                    <Typography component="div">
                        <CircularProgress sx={{ marginRight: '1rem' }} /> Retrieving application list...
                    </Typography>
                </RenderIf>
            </Paper>
        </Box>
    );
};

export default SchoolDashboard;
